// src/pages/facilities/training/trainingText.js
//
// R-TRAINING slice 1 (spec §9) — the words the training pages draw. The
// register's state, its sentences and every refusal are the SERVER's
// (`register_cell` computes each cell; no client math); what lives here
// is only how they are laid out: the state's word and tint, the cell's
// short line under a column that already names the target, and the
// review item split into its sentence and its link.
import { toArray } from '../../../hooks/useRackTree';
import {
  listRooms, getRacksAndEnclosuresByFacility, getEnclosures,
  getSpeciesByFacility, getAnimalsByFacility,
} from '../../../services/api';
import { formatSpeciesName } from '../../../utils/scientificName';
import { apiDay } from '../../../utils/dateUtils';

const RANK = { Owner: 3, Manager: 2, Staff: 1 };

/** The viewer's role at this facility: their HIGHEST row (a user can hold
 * two rows; the server reads the highest too). */
export const roleAt = (roles, facilityId) => (roles || [])
  .filter((r) => String(r.facility) === String(facilityId))
  .reduce((best, r) => ((RANK[r.role] || 0) > (RANK[best] || 0) ? r.role : best), null);

/** The grid's columns (Q-training-grid-width): the targets the SERVER
 * marks in use (`in_use`, `training.register_targets` — the page does no
 * math), or every target once "Show all" is on. A target with no `in_use`
 * key is shown. */
export const gridTargets = (targets, showAll) => (
  showAll ? (targets || []) : (targets || []).filter((t) => t.in_use !== false));

/** A cell's state → its word and its badge tint (badges are tints). */
export const STATE_WORDS = {
  signed_off: { word: 'Signed off', tint: 'success' },
  in_training: { word: 'In training', tint: 'info' },
  voided: { word: 'Voided', tint: 'warning' },
  not_started: { word: 'Not started', tint: 'neutral' },
};

export const stateOf = (cell) => STATE_WORDS[cell && cell.state] || STATE_WORDS.not_started;

/**
 * The server's sentence without its target name: under a column headed
 * "Feeding", `Feeding — 2 sessions (last 11/2 with Allen), not signed
 * off` reads `2 sessions (last 11/2 with Allen), not signed off`. Any
 * other shape is shown whole.
 */
export const cellLine = (cell, targetName) => {
  const sentence = String((cell && cell.sentence) || '');
  const prefix = `${targetName} — `;
  return targetName && sentence.startsWith(prefix) ? sentence.slice(prefix.length) : sentence;
};

/** `2026-11-03T15:00:00Z` (or `2026-11-03`) → `11/3`, the local day. */
export const shortDay = (value) => {
  if (!value) return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [, m, d] = text.split('-').map(Number);
    return `${m}/${d}`;
  }
  const at = new Date(text);
  if (Number.isNaN(at.getTime())) return '';
  return `${at.getMonth() + 1}/${at.getDate()}`;
};

/** One sign-off row of the sheet's history, as a sentence. */
export const signoffLine = (row) => {
  if (!row) return '';
  const where = row.scope_label ? ` (${row.scope_label})` : ' (everywhere)';
  let line = `Signed off ${shortDay(row.signed_at)} by ${row.signed_by_name || 'someone'}${where}`;
  if (row.voided_at) {
    const by = row.voided_by_name ? ` by ${row.voided_by_name}` : '';
    line += `, voided ${shortDay(row.voided_at)}${by}: ${row.void_reason || 'no reason given'}`;
  }
  return line;
};

/** A session row → `Training shift 11/2 with Allen — 2 done`. */
export const sessionLine = (session) => {
  const day = shortDay(session && session.day);
  const who = session && session.trainer_name ? ` with ${session.trainer_name}` : '';
  const n = Number(session && session.count) || 0;
  return `Training shift ${day}${who} — ${n} done`;
};

/**
 * A review item's `text` ends `… before sign-off on Feeding. Feeding
 * history: https://…/feeding?tab=history` (the email's own words). On the
 * page the URL is a link: `{head: '… before sign-off on Feeding', label:
 * 'Feeding history'}`. A BEHIND line has no URL: `{head: text, label:
 * null}`.
 */
export const splitReviewText = (text) => {
  const raw = String(text || '');
  const match = raw.match(/^([\s\S]*)\. ([^.:]+): (\S+)$/);
  if (match) return { head: match[1], label: match[2] };
  return { head: raw, label: null };
};

/** The scope control's choices (the server's `scope_kind`s). */
export const SCOPE_KINDS = [
  { value: 'none', label: 'Everywhere' },
  { value: 'room', label: 'A room' },
  { value: 'rack', label: 'A rack' },
  { value: 'enclosure', label: 'A tank' },
  { value: 'species', label: 'A species' },
  { value: 'animal', label: 'An animal' },
];

export const SCOPE_NOUN = {
  room: 'room', rack: 'rack', enclosure: 'tank', species: 'species', animal: 'animal',
};

export const GENDER_OPTIONS = [
  { value: 'unsaid', label: 'Not said' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
];

export const genderLabel = (value) => (
  (GENDER_OPTIONS.find((g) => g.value === value) || GENDER_OPTIONS[0]).label);

/**
 * The places / species / animals a sign-off can be scoped to, read off the
 * facility's own lists when the kind is picked: `[{id, label}]`. A tank
 * names its rack ("Rack 2 · 2.1A"). A list that cannot be read is empty,
 * never a guess.
 */
export const loadScopeOptions = async (facilityId, kind) => {
  const sorted = (rows) => rows
    .filter((r) => r.id !== null && r.id !== undefined && r.label)
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  if (kind === 'room') {
    const rows = toArray(await listRooms(facilityId));
    return sorted(rows.map((r) => ({ id: String(r.id), label: r.name || '' })));
  }
  if (kind === 'rack') {
    const rows = toArray(await getRacksAndEnclosuresByFacility(facilityId));
    return sorted(rows.map((r) => ({ id: String(r.id), label: r.name || '' })));
  }
  if (kind === 'enclosure') {
    const rows = toArray(await getEnclosures(facilityId));
    return sorted(rows.map((e) => ({
      id: String(e.id),
      label: e.rack_name ? `${e.rack_name} · ${e.name || ''}` : (e.name || ''),
    })));
  }
  if (kind === 'species') {
    const rows = toArray(await getSpeciesByFacility(facilityId));
    return sorted(rows.map((s) => ({ id: String(s.id), label: formatSpeciesName(s) || '' })));
  }
  if (kind === 'animal') {
    const rows = toArray(await getAnimalsByFacility(facilityId));
    return sorted(rows.map((a) => ({ id: String(a.id), label: a.name || '' })));
  }
  return [];
};

/** The sentence under the Birthday field for a day after today. */
export const BIRTHDAY_IN_FUTURE = "A birthday can't be in the future.";

/** A birthday typed on the keeper tab: '' (none), or a refusal sentence.
 * The server's own rules, said before the request (years below 1000 and a
 * future day are refused at both ends). "Today" is the day the API judges
 * in (`apiDay`, the facility's zone) — never the UTC day; the server's
 * check is `facility_today`. Both are `YYYY-MM-DD` — and they compare as
 * strings ONLY because the year is held to exactly four digits first: a
 * date input takes a five-digit year ("20140-01-01" sorts below
 * "2026-11-04"), so a year above 9999 is refused with the four-digit
 * sentence before any comparison. */
export const birthdayRefusal = (value, today = apiDay()) => {
  if (!value) return '';
  const [y] = String(value).split('-').map(Number);
  if (!y || y < 1000 || y > 9999) return 'A year has four digits.';
  if (String(value) > today) return BIRTHDAY_IN_FUTURE;
  return '';
};
